import React, { useState, useEffect } from 'react';
import { 
  collection, 
  onSnapshot, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import MarkdownRenderer from './MarkdownRenderer';
import { Edit2, Trash2, Upload, Link as LinkIcon, Image, CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [imageUploadMode, setImageUploadMode] = useState('url'); // 'url' or 'upload'
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({ show: false, type: '', title: '', message: '', onConfirm: null });
  const { isAdmin } = useAuth();

  const ARTICLES_PER_PAGE = 10;

  // Show confirmation modal function
  const showConfirmationModal = (type, title, message, onConfirm = null) => {
    setConfirmationModal({ show: true, type, title, message, onConfirm });
  };

  // Hide confirmation modal function
  const hideConfirmationModal = () => {
    setConfirmationModal({ show: false, type: '', title: '', message: '', onConfirm: null });
  };

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    categories: [], // Start with empty array for new articles
    imageUrl: ''
  });

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Convert image to base64 for storage (for demo purposes)
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Clear image selection
  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview('');
    setFormData(prev => ({...prev, imageUrl: ''}));
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      categories: [],
      imageUrl: ''
    });
    setEditingArticle(null);
    setShowForm(false);
    setSelectedImage(null);
    setImagePreview('');
    setImageUploadMode('url');
    setShowAddCategory(false);
    setNewCategory('');
  };

  // Function specifically for creating new article
  const startNewArticle = () => {
    // Reset all form state
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      categories: [],
      imageUrl: ''
    });
    setEditingArticle(null);
    setSelectedImage(null);
    setImagePreview('');
    setImageUploadMode('url');
    setShowAddCategory(false);
    setNewCategory('');
    // Then show the form
    setShowForm(true);
  };

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategory.trim() || categories.includes(newCategory.trim().toLowerCase())) {
      return;
    }
    
    const category = newCategory.trim().toLowerCase();
    
    try {
      // Add to database
      const categoryRef = doc(db, 'categories', category);
      await setDoc(categoryRef, {
        name: category,
        createdAt: serverTimestamp()
      });
      
      // Update local state
      setCategories(prev => [...prev, category].sort());
      setNewCategory('');
      setShowAddCategory(false);
      
      showConfirmationModal('success', 'Success', `Category "${category}" added successfully!`);
    } catch (error) {
      console.error('Error adding category:', error);
      showConfirmationModal('error', 'Error', 'Error adding category. Please try again.');
    }
  };

  // Check if category is used by any articles
  const checkCategoryUsage = async (categoryToCheck) => {
    try {
      const q = query(collection(db, 'articles'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const articlesUsingCategory = snapshot.docs.filter(doc => {
        const article = doc.data();
        if (article.categories && Array.isArray(article.categories)) {
          return article.categories.includes(categoryToCheck);
        } else if (article.category) {
          return article.category === categoryToCheck;
        }
        return false;
      });
      
      return articlesUsingCategory.length;
    } catch (error) {
      console.error('Error checking category usage:', error);
      return -1;
    }
  };

  // Handle category deletion
  const handleDeleteCategory = async (categoryName) => {
    const articlesCount = await checkCategoryUsage(categoryName);
    
    if (articlesCount === -1) {
      showConfirmationModal('error', 'Error', 'Error checking category usage. Please try again.');
      return;
    }
    
    if (articlesCount > 0) {
      showConfirmationModal('warning', 'Cannot Delete Category', `Cannot delete category "${categoryName}". It is used by ${articlesCount} article${articlesCount > 1 ? 's' : ''}. Please remove this category from all articles first.`);
      return;
    }
    
    setCategoryToDelete(categoryName);
    setShowDeleteCategoryModal(true);
  };

  // Confirm category deletion
  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    try {
      setDeletingCategory(true);
      
      // Double-check that no articles use this category
      const articlesCount = await checkCategoryUsage(categoryToDelete);
      
      if (articlesCount > 0) {
        showConfirmationModal('warning', 'Cannot Delete Category', `Cannot delete category "${categoryToDelete}". It is now used by ${articlesCount} article${articlesCount > 1 ? 's' : ''}.`);
        setShowDeleteCategoryModal(false);
        setCategoryToDelete(null);
        setDeletingCategory(false);
        return;
      }
      
      // Remove from database
      const categoryRef = doc(db, 'categories', categoryToDelete);
      await deleteDoc(categoryRef);
      
      // Remove category from the list
      setCategories(prev => prev.filter(cat => cat !== categoryToDelete));
      
      // Update form data if the deleted category was selected
      setFormData(prev => ({
        ...prev,
        categories: prev.categories.filter(cat => cat !== categoryToDelete)
      }));
      
      // Ensure at least one category is selected in form
      if (formData.categories.filter(cat => cat !== categoryToDelete).length === 0) {
        setFormData(prev => ({
          ...prev,
          categories: []
        }));
      }
      
      // Close modal and reset state
      setShowDeleteCategoryModal(false);
      setCategoryToDelete(null);
      setDeletingCategory(false);
      
      showConfirmationModal('success', 'Success', `Category "${categoryToDelete}" has been deleted successfully.`);
      
    } catch (error) {
      console.error('Error deleting category:', error);
      showConfirmationModal('error', 'Error', 'Error deleting category. Please try again.');
      setDeletingCategory(false);
    }
  };

  // Cancel category deletion
  const cancelDeleteCategory = () => {
    setShowDeleteCategoryModal(false);
    setCategoryToDelete(null);
  };

  // Handle category selection (multiple)
  const handleCategoryChange = (category) => {
    setFormData(prev => {
      const currentCategories = prev.categories || [];
      const isSelected = currentCategories.includes(category);
      
      if (isSelected) {
        // Don't allow removing if it's the last category
        if (currentCategories.length === 1) return prev;
        return {
          ...prev,
          categories: currentCategories.filter(c => c !== category)
        };
      } else {
        return {
          ...prev,
          categories: [...currentCategories, category]
        };
      }
    });
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load categories from database
  useEffect(() => {
    loadCategories();
  }, []);

  // Load initial articles
  useEffect(() => {
    loadInitialArticles();
  }, []);

  // Initialize default categories in database if they don't exist
  const initializeDefaultCategories = async () => {
    const defaultCategories = ['nutrition', 'yoga', 'herbs', 'lifestyle', 'treatment'];
    
    try {
      for (const category of defaultCategories) {
        const categoryRef = doc(db, 'categories', category);
        await setDoc(categoryRef, {
          name: category,
          createdAt: serverTimestamp()
        }, { merge: true }); // merge: true prevents overwriting if it already exists
      }
    } catch (error) {
      console.error('Error initializing default categories:', error);
    }
  };

  // Load categories from database
  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      
      // Check if categories collection exists by trying to get documents
      const categoriesQuery = query(collection(db, 'categories'), orderBy('name'));
      const snapshot = await getDocs(categoriesQuery);
      
      if (snapshot.empty) {
        // Initialize default categories if none exist
        await initializeDefaultCategories();
        // Reload after initialization
        const newSnapshot = await getDocs(categoriesQuery);
        const categoriesData = newSnapshot.docs.map(doc => doc.data().name);
        setCategories(categoriesData);
      } else {
        const categoriesData = snapshot.docs.map(doc => doc.data().name);
        setCategories(categoriesData);
      }
      
      setLoadingCategories(false);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to default categories if there's an error
      setCategories(['nutrition', 'yoga', 'herbs', 'lifestyle', 'treatment']);
      setLoadingCategories(false);
    }
  };

  const loadInitialArticles = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'articles'),
        orderBy('createdAt', 'desc'),
        limit(ARTICLES_PER_PAGE)
      );
      
      const snapshot = await getDocs(q);
      const articlesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setArticles(articlesData);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === ARTICLES_PER_PAGE);
      setLoading(false);
    } catch (error) {
      console.error('Error loading articles:', error);
      setLoading(false);
    }
  };

  const loadMoreArticles = async () => {
    if (!lastVisible || loadingMore) return;
    
    try {
      setLoadingMore(true);
      const q = query(
        collection(db, 'articles'),
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible),
        limit(ARTICLES_PER_PAGE)
      );
      
      const snapshot = await getDocs(q);
      const newArticles = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setArticles(prev => [...prev, ...newArticles]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === ARTICLES_PER_PAGE);
      setLoadingMore(false);
    } catch (error) {
      console.error('Error loading more articles:', error);
      setLoadingMore(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let imageUrl = formData.imageUrl;
      
      // If user uploaded an image, convert to base64 (in production, you'd upload to Firebase Storage)
      if (imageUploadMode === 'upload' && selectedImage) {
        imageUrl = await convertImageToBase64(selectedImage);
      }

      const articleData = {
        ...formData,
        imageUrl: imageUrl,
        categories: formData.categories && formData.categories.length > 0 ? formData.categories : ['lifestyle'] // Ensure at least one category
      };

      if (editingArticle) {
        // Update existing article
        const articleRef = doc(db, 'articles', editingArticle.id);
        await updateDoc(articleRef, {
          ...articleData,
          updatedAt: serverTimestamp()
        });
        showConfirmationModal('success', 'Success', 'Article updated successfully!');
      } else {
        // Create new article
        await addDoc(collection(db, 'articles'), {
          ...articleData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          likesCount: 0,
          likedBy: [],
          comments: []
        });
        showConfirmationModal('success', 'Success', 'Article created successfully!');
      }

      // Reset form
      resetForm();
      
      // Reload articles to show the new/updated article
      loadInitialArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      showConfirmationModal('error', 'Error', 'Failed to save article. Please try again.');
    }
  };

  const handleEdit = (article) => {
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      categories: article.categories || [article.category] || ['lifestyle'], // Handle both old and new format
      imageUrl: article.imageUrl || ''
    });
    
    // Set image preview if there's an existing image
    if (article.imageUrl) {
      setImagePreview(article.imageUrl);
      setImageUploadMode(article.imageUrl.startsWith('data:') ? 'upload' : 'url');
    } else {
      setImagePreview('');
      setImageUploadMode('url');
    }
    
    setEditingArticle(article);
    setShowForm(true);
  };

  const handleDelete = async (articleId) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await deleteDoc(doc(db, 'articles', articleId));
        showConfirmationModal('success', 'Success', 'Article deleted successfully!');
        // Reload articles after deletion
        loadInitialArticles();
      } catch (error) {
        console.error('Error deleting article:', error);
        showConfirmationModal('error', 'Error', 'Failed to delete article. Please try again.');
      }
    }
  };

  const cancelEdit = () => {
    resetForm();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50">
      {/* Confirmation Modal */}
      <ConfirmationModal 
        modal={confirmationModal} 
        onClose={hideConfirmationModal} 
      />

      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Article Management</h1>
              <p className="text-gray-600 mt-2">Create and manage your blog articles</p>
            </div>
            <div className="flex gap-4">
              <Link
                to="/blog"
                className="px-4 py-2 text-green-600 hover:text-green-800 font-medium"
              >
                View Blog
              </Link>
              <button
                onClick={startNewArticle}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                + New Article
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Article Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingArticle ? 'Edit Article' : 'Create New Article'}
                  </h2>
                  <button
                    onClick={cancelEdit}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter article title"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Categories (select at least one)
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowAddCategory(!showAddCategory)}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        + Add Category
                      </button>
                    </div>
                    
                    {/* Add New Category */}
                    {showAddCategory && (
                      <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            placeholder="Enter new category name"
                            className="flex-1 p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                          />
                          <button
                            type="button"
                            onClick={handleAddCategory}
                            className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddCategory(false);
                              setNewCategory('');
                            }}
                            className="px-3 py-2 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Category Selection */}
                    <div className="grid grid-cols-2 gap-2">
                      {categories.map(category => (
                        <div
                          key={category}
                          className="relative group"
                        >
                          <label
                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                              formData.categories?.includes(category)
                                ? 'bg-green-50 border-green-300 text-green-800'
                                : 'bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.categories?.includes(category) || false}
                              onChange={() => handleCategoryChange(category)}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium">
                              {category.charAt(0).toUpperCase() + category.slice(1)}
                            </span>
                            {formData.categories?.includes(category) && (
                              <span className="ml-auto text-green-600">✓</span>
                            )}
                          </label>
                          
                          {/* Delete Category Button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCategory(category);
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100 flex items-center justify-center"
                            title={`Delete ${category} category`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    {(!formData.categories || formData.categories.length === 0) && (
                      <p className="text-red-500 text-sm mt-1">Please select at least one category</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Article Image
                    </label>
                    
                    {/* Image Mode Toggle */}
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setImageUploadMode('url')}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          imageUploadMode === 'url'
                            ? 'bg-green-100 text-green-800 border border-green-300'
                            : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        <LinkIcon size={16} />
                        URL
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageUploadMode('upload')}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          imageUploadMode === 'upload'
                            ? 'bg-green-100 text-green-800 border border-green-300'
                            : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        <Upload size={16} />
                        Upload
                      </button>
                    </div>

                    {/* URL Input */}
                    {imageUploadMode === 'url' && (
                      <input
                        type="url"
                        value={formData.imageUrl}
                        onChange={(e) => {
                          setFormData({...formData, imageUrl: e.target.value});
                          setImagePreview(e.target.value);
                          clearImage();
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="https://example.com/image.jpg"
                      />
                    )}

                    {/* File Upload */}
                    {imageUploadMode === 'upload' && (
                      <div className="space-y-3">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            className="hidden"
                            id="image-upload"
                          />
                          <label
                            htmlFor="image-upload"
                            className="cursor-pointer flex flex-col items-center gap-2"
                          >
                            <Image size={32} className="text-gray-400" />
                            <span className="text-sm text-gray-600">
                              Click to upload an image or drag and drop
                            </span>
                            <span className="text-xs text-gray-500">
                              PNG, JPG, GIF up to 10MB
                            </span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Image Preview */}
                    {imagePreview && (
                      <div className="mt-3">
                        <div className="relative inline-block">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-32 h-24 object-cover rounded-lg border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={clearImage}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            ✕
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Image preview</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excerpt
                    </label>
                    <textarea
                      required
                      value={formData.excerpt}
                      onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      rows={3}
                      placeholder="Brief description of the article"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content (Markdown supported)
                    </label>
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <MDEditor
                        key={editingArticle ? `edit-${editingArticle.id}` : 'new-article'}
                        value={formData.content}
                        onChange={(value) => setFormData({...formData, content: value || ''})}
                        preview="edit"
                        height={400}
                        data-color-mode="light"
                        hideToolbar={false}
                        visibleDragBar={false}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      You can use Markdown syntax for rich formatting. Use the preview tab to see how it will look.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={!formData.categories || formData.categories.length === 0}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 ${
                        !formData.categories || formData.categories.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 hover:shadow-lg'
                      }`}
                    >
                      {editingArticle ? 'Update Article' : 'Create Article'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="flex-1 py-3 text-gray-600 hover:text-white hover:bg-gray-600 border border-gray-400 rounded-lg font-medium transition-all duration-200 hover:shadow-md"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Articles List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              All Articles (showing {articles.length})
            </h2>
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles yet</h3>
              <p className="text-gray-600 mb-4">Create your first article to get started!</p>
              <button
                onClick={startNewArticle}
                className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-medium transition-all duration-200 hover:shadow-lg"
              >
                Create Article
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {articles.map((article) => (
                <div key={article.id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {article.title}
                        </h3>
                        <div className="flex gap-1 flex-wrap">
                          {(article.categories || [article.category]).filter(Boolean).map((category, index) => (
                            <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                              {category}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-3 line-clamp-2">
                        {article.excerpt}
                      </p>
                      
                      <div className="flex items-center gap-6 text-sm text-gray-500">
                        <span>
                          {article.createdAt?.toDate().toLocaleDateString()}
                        </span>
                        <span>❤️ {article.likesCount || 0}</span>
                        <span>💬 {article.comments?.length || 0}</span>
                      </div>
                    </div>

                    <div className="flex gap-3 ml-4">
                      <button
                        onClick={() => handleEdit(article)}
                        className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-600 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
                      >
                        <Edit2 size={16} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-white hover:bg-red-600 border border-red-600 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Load More Button */}
              {hasMore && (
                <div className="p-6 text-center border-t border-gray-200">
                  <button
                    onClick={loadMoreArticles}
                    disabled={loadingMore}
                    className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                      loadingMore
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 hover:shadow-lg'
                    }`}
                  >
                    {loadingMore ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                        Loading...
                      </div>
                    ) : (
                      `Load More Articles`
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Category Confirmation Modal */}
      {showDeleteCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Category
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the category "{categoryToDelete}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDeleteCategory}
                disabled={deletingCategory}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteCategory}
                disabled={deletingCategory}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {deletingCategory ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Category'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Confirmation Modal Component (similar to Delete Category Modal)
const ConfirmationModal = ({ modal, onClose }) => {
  if (!modal.show) return null;

  const getModalConfig = () => {
    switch (modal.type) {
      case 'success':
        return {
          icon: CheckCircle,
          iconColor: 'text-green-600',
          buttonColor: 'bg-green-600 hover:bg-green-700'
        };
      case 'error':
        return {
          icon: XCircle,
          iconColor: 'text-red-600',
          buttonColor: 'bg-red-600 hover:bg-red-700'
        };
      case 'warning':
        return {
          icon: AlertCircle,
          iconColor: 'text-yellow-600',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700'
        };
      default:
        return {
          icon: AlertCircle,
          iconColor: 'text-blue-600',
          buttonColor: 'bg-blue-600 hover:bg-blue-700'
        };
    }
  };

  const config = getModalConfig();
  const IconComponent = config.icon;

  const handleConfirm = () => {
    if (modal.onConfirm) {
      modal.onConfirm();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-start mb-4">
          <IconComponent className={`${config.iconColor} h-6 w-6 mt-1 mr-3 flex-shrink-0`} />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {modal.title}
            </h3>
            <p className="text-gray-600">
              {modal.message}
            </p>
          </div>
        </div>
        
        <div className="flex gap-3 justify-end">
          {modal.onConfirm ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className={`px-4 py-2 ${config.buttonColor} text-white rounded-lg font-medium`}
              >
                Confirm
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className={`px-4 py-2 ${config.buttonColor} text-white rounded-lg font-medium`}
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminArticles;
